import {
    BaseArea,
    BaseInput,
    BaseSelect,
    CurrencyType,
    DatePicker,
    formatDate,
    ISelectOption,
    ProviderCreateCollectionPaymentPayload,
    ProviderPatientStatement,
    IToastContextProps,
} from '@enter/core';
import styled from '@emotion/styled';
import { format } from 'date-fns';
import _ from 'lodash';
import React, { useState } from 'react';
import { IClaimStore, IDialogContext } from './statement-details.types';
import { ButtonBar } from '../button-bar';
import * as S from './record-payment-dialog.styles';

const FormSection = styled.div`
    margin-top: 16px;
`;

type RecordPaymentDialogProps = {
    patientStatement?: ProviderPatientStatement;
    onClose: () => void;
    onSuccess: () => void;
    claimStore: IClaimStore;
    dialogContext: IDialogContext;
    toastContext: IToastContextProps;
};

export const RecordPaymentDialog: React.FunctionComponent<RecordPaymentDialogProps> = (props) => {
    const { claimStore, dialogContext, toastContext, patientStatement, onClose, onSuccess } = props;

            // Initialize form state with defaults
            const [paymentDate, setPaymentDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
            const [amount, setAmount] = useState<string>('');
            const [claimId, setClaimId] = useState<string>('');
            const [memo, setMemo] = useState<string>('');
            const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
            const [errors, setErrors] = useState<Record<string, string>>({});

            // Build claim options from patient statement
            const claimOptions: ISelectOption[] = React.useMemo(() => {
                if (!patientStatement?.claims) return [];
                return patientStatement.claims.map(claim => ({
                    label: `${claim.externalId} - ${claim.fromDos ? formatDate(claim.fromDos, 'MMM dd, yyyy') : ''}`,
                    value: claim.id,
                }));
            }, [patientStatement]);

            const validate = (): boolean => {
                const newErrors: Record<string, string> = {};

                if (!paymentDate) {
                    newErrors.paymentDate = 'Payment date is required';
                }

                if (!amount || parseFloat(amount) <= 0) {
                    newErrors.amount = 'Amount is required and must be greater than 0';
                }

                if (!claimId) {
                    newErrors.claimId = 'Claim is required';
                }

                setErrors(newErrors);
                return Object.keys(newErrors).length === 0;
            };

            const handleSubmit = async (): Promise<void> => {
                if (!validate()) {
                    return;
                }

                setIsSubmitting(true);

                const payload: ProviderCreateCollectionPaymentPayload = {
                    collectedAmount: {
                        amount: amount,
                        currency: CurrencyType.USD,
                    },
                    claimId,
                    paymentDate,
                    memo: memo || undefined,
                };

                try {
                    await claimStore.createCollectionPayment(payload);
                    toastContext.notify?.('Payment was recorded successfully.', 'right');
                    onSuccess();
                } catch (err) {
                    dialogContext.showError(err);
                } finally {
                    setIsSubmitting(false);
                }
            };

            const handleCancel = (): void => {
                onClose();
            };

            const handleDateChange = (date: Date | undefined): void => {
                if (date) {
                    setPaymentDate(format(date, 'yyyy-MM-dd'));
                } else {
                    setPaymentDate('');
                }
            };

            const handleClaimChange = (option: any): void => {
                setClaimId(option?.value as string || '');
            };

            return (
                <S.DialogBody>
                    <S.InputRow>
                        <S.InputName>
                            <S.Label>Payment Date</S.Label>
                            <DatePicker
                                value={paymentDate}
                                onDateSelected={handleDateChange}
                                errorMessage={errors.paymentDate}
                            />
                        </S.InputName>
                        <S.InputAmount>
                            <S.Label>Amount</S.Label>
                            <BaseInput
                                type="number"
                                value={amount}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
                                errorMessage={errors.amount}
                                placeholder="0.00"
                            />
                        </S.InputAmount>
                    </S.InputRow>

                    <FormSection>
                        <S.Label>Claim To Post Payment To</S.Label>
                        <BaseSelect
                            options={claimOptions}
                            value={claimOptions.find(opt => opt.value === claimId)}
                            onChange={handleClaimChange}
                            errorMessage={errors.claimId}
                            placeholder="Select a claim"
                            required
                        />
                    </FormSection>

                    <S.InputMemo>
                        <S.Label>Payment Memo</S.Label>
                        <BaseArea
                            value={memo}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMemo(e.target.value)}
                            placeholder="Enter payment notes..."
                            rows={4}
                        />
                    </S.InputMemo>

                    <S.ButtonGroup
                        changed={true}
                        useNotify={false}
                        onCancel={handleCancel}
                        onSave={handleSubmit}
                        saveLabel={isSubmitting ? 'Creating...' : 'Create Payment'}
                        saveNotice="Payment recorded successfully"
                    />
                </S.DialogBody>
            );
};

